import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import BackButton from '../../components/BackButton';
import { useAuth } from '../../contexts/AuthContext';
import {
  Building2, Users, Package, FileText, Truck,
  Receipt, TrendingUp, ClipboardList, Landmark, LayoutDashboard, Bell
} from 'lucide-react';

const cards = [
  { title: 'Dashboard financiero', desc: 'Facturado, cobros, IVA, top clientes y evolución mensual', icon: LayoutDashboard, to: '/billing/dashboard', color: 'bg-indigo-100 text-indigo-700', group: 'Analítica' },
  { title: 'Revisión de cobros', desc: 'Facturas vencidas y próximas + recordatorios automáticos', icon: Bell, to: '/billing/payment-review', color: 'bg-rose-100 text-rose-700', group: 'Analítica' },

  { title: 'Presupuestos', desc: 'Ofertas a clientes con PDF y conversión a factura', icon: ClipboardList, to: '/billing/quotes', color: 'bg-blue-100 text-blue-700', group: 'Ventas' },
  { title: 'Albaranes', desc: 'Notas de entrega convertibles en factura', icon: Receipt, to: '/billing/delivery-notes', color: 'bg-cyan-100 text-cyan-700', group: 'Ventas' },
  { title: 'Facturas', desc: 'Emisión, PDF, envío y firma FACe (Facturae 3.2.2 XAdES)', icon: FileText, to: '/billing/invoices', color: 'bg-emerald-100 text-emerald-700', group: 'Ventas' },

  { title: 'Pedidos a proveedores', desc: 'Órdenes de compra con PDF', icon: Truck, to: '/billing/purchase-orders', color: 'bg-amber-100 text-amber-700', group: 'Compras' },
  { title: 'Gastos', desc: 'Facturas y recibos recibidos con adjuntos', icon: TrendingUp, to: '/billing/expenses', color: 'bg-orange-100 text-orange-700', group: 'Compras' },

  { title: 'Clientes', desc: 'Empresas, particulares y entes públicos (FACe)', icon: Users, to: '/billing/clients', color: 'bg-slate-100 text-slate-700', group: 'Maestros' },
  { title: 'Proveedores', desc: 'Empresas y autónomos con los que colaboras', icon: Truck, to: '/billing/suppliers', color: 'bg-slate-100 text-slate-700', group: 'Maestros' },
  { title: 'Productos y servicios', desc: 'Catálogo facturable (incluye cursos del LMS)', icon: Package, to: '/billing/products', color: 'bg-slate-100 text-slate-700', group: 'Maestros' },
  { title: 'Ajustes de facturación', desc: 'Datos fiscales, series de numeración, IVA e IBAN', icon: Building2, to: '/billing/settings', color: 'bg-slate-100 text-slate-700', group: 'Maestros' },
];

const upcomingCards = [];

const BillingHub = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  if (user && user.role !== 'Administrador') {
    return (
      <Layout>
        <div className="max-w-md mx-auto text-center py-16">
          <Building2 className="w-12 h-12 mx-auto text-slate-400 mb-4" />
          <h2 className="text-xl font-semibold text-slate-900">Acceso restringido</h2>
          <p className="text-slate-500 mt-2">La Intranet de Gestión solo está disponible para administradores.</p>
        </div>
      </Layout>
    );
  }

  const groups = ['Analítica', 'Ventas', 'Compras', 'Maestros'];

  return (
    <Layout>
      <div className="space-y-8" data-testid="billing-hub-page">
        <div>
          <BackButton to="/dashboard" label="Volver al Dashboard" />
          <h1 className="text-3xl font-manrope font-bold text-slate-900">Intranet de gestión</h1>
          <p className="text-slate-500 mt-1">Facturación, presupuestos, compras y control financiero de IRTA</p>
        </div>

        {groups.map(g => {
          const items = cards.filter(c => c.group === g);
          if (items.length === 0) return null;
          return (
            <div key={g}>
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">{g}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map(c => {
                  const Icon = c.icon;
                  return (
                    <Link key={c.to} to={c.to} data-testid={`billing-hub-card-${c.to.split('/').pop()}`} className="group bg-white border border-slate-200 rounded-xl p-6 hover:border-[#004E89] hover:shadow-md transition-all">
                      <div className={`w-12 h-12 rounded-lg ${c.color} flex items-center justify-center mb-4`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <h3 className="font-manrope font-semibold text-slate-900 group-hover:text-[#004E89]">{c.title}</h3>
                      <p className="text-sm text-slate-500 mt-1">{c.desc}</p>
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}

        {upcomingCards.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Próximas fases</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {upcomingCards.map(c => {
                const Icon = c.icon;
                return (
                  <div key={c.title} className="bg-slate-50 border border-dashed border-slate-300 rounded-xl p-6 opacity-70">
                    <div className="w-12 h-12 rounded-lg bg-slate-200 text-slate-500 flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="font-manrope font-semibold text-slate-700">{c.title}</h3>
                    <p className="text-sm text-slate-500 mt-1">{c.desc}</p>
                    <span className="inline-block mt-3 text-xs px-2 py-1 rounded-full bg-slate-200 text-slate-600">Próximamente</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BillingHub;
